import React, { useState } from 'react';
import { diagnosticsService, DiagnosticResult } from '../services/diagnosticsService';
import { diagnosticsConfig, updateEmulatorEndpoint, getEmulatorStatus } from '../config/diagnostics';

interface DiagnosticsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DiagnosticsPanel: React.FC<DiagnosticsPanelProps> = ({ isOpen, onClose }) => {
  const [testResults, setTestResults] = useState<DiagnosticResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [customHost, setCustomHost] = useState(diagnosticsConfig.emulatorHost);
  const [customPort, setCustomPort] = useState(diagnosticsConfig.emulatorPort.toString());

  if (!isOpen) return null;

  const runDiagnostics = async () => {
    setIsRunning(true);
    try {
      const results = await diagnosticsService.runAuthEmulatorConnectivityTest();
      setTestResults(results);
    } catch (error) {
      console.error('Diagnostics failed:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const updateEmulatorSettings = () => {
    const port = parseInt(customPort);
    if (isNaN(port) || port < 1 || port > 65535) {
      alert('Please enter a valid port number (1-65535)');
      return;
    }
    
    updateEmulatorEndpoint(customHost, port);
    setTestResults([]); // Clear previous results
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'running': return '🔄';
      default: return '⏳';
    }
  };

  const emulatorInfo = diagnosticsService.getEmulatorInfo();
  const emulatorStatus = getEmulatorStatus();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">Firebase Emulator Diagnostics</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Emulator Configuration */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-700 mb-3">Current Emulator Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <strong>Endpoint:</strong> {emulatorInfo.endpoint}
              </div>
              <div>
                <strong>Host:</strong> {diagnosticsConfig.emulatorHost}
              </div>
              <div>
                <strong>Port:</strong> {diagnosticsConfig.emulatorPort}
              </div>
              <div>
                <strong>Project ID:</strong> {emulatorInfo.connected}
              </div>
              <div>
                <strong>Using Emulator:</strong> 
                <span className={`ml-2 px-2 py-1 rounded text-xs ${
                  emulatorStatus.isUsingEmulator 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {emulatorStatus.isUsingEmulator ? 'Yes' : 'No'}
                </span>
              </div>
              <div>
                <strong>Current User:</strong> {emulatorInfo.currentUser}
              </div>
            </div>
          </div>

          {/* Runtime Emulator Configuration */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-blue-800 mb-3">Change Emulator Settings</h3>
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-blue-700 mb-1">Host</label>
                <input
                  type="text"
                  value={customHost}
                  onChange={(e) => setCustomHost(e.target.value)}
                  placeholder="localhost"
                  className="w-full px-3 py-2 border border-blue-300 rounded-md text-sm"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-blue-700 mb-1">Port</label>
                <input
                  type="number"
                  value={customPort}
                  onChange={(e) => setCustomPort(e.target.value)}
                  placeholder="9099"
                  min="1"
                  max="65535"
                  className="w-full px-3 py-2 border border-blue-300 rounded-md text-sm"
                />
              </div>
              <button
                onClick={updateEmulatorSettings}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-sm font-medium"
              >
                Update
              </button>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              Changes take effect immediately. Clear results and run tests again after updating.
            </p>
          </div>

          {/* Environment Variables */}
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="font-semibold text-yellow-800 mb-3">Environment Variables</h3>
            <div className="text-xs space-y-1 font-mono">
              {Object.entries(emulatorStatus.availableEnvVars).map(([key, value]) => (
                <div key={key} className="flex">
                  <span className="text-yellow-700 w-80">{key}:</span>
                  <span className={value ? 'text-green-700' : 'text-gray-400'}>
                    {value || 'not set'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Test Controls */}
          <div className="flex space-x-4">
            <button
              onClick={runDiagnostics}
              disabled={isRunning}
              className={`px-4 py-2 rounded-lg font-medium ${
                isRunning
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-emerald-500 hover:bg-emerald-600 text-white'
              }`}
            >
              {isRunning ? '🔄 Running Tests...' : '🧪 Run Connectivity Tests'}
            </button>
            
            <button
              onClick={() => setTestResults([])}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
            >
              Clear Results
            </button>
          </div>

          {/* Test Results */}
          {testResults.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-700">Test Results</h3>
              {testResults.map((result, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-l-4 ${
                    result.status === 'success'
                      ? 'bg-green-50 border-green-500'
                      : result.status === 'error'
                      ? 'bg-red-50 border-red-500'
                      : 'bg-blue-50 border-blue-500'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">{getStatusIcon(result.status)}</span>
                      <div>
                        <div className="font-medium text-gray-800">{result.test}</div>
                        <div className="text-sm text-gray-600 mt-1">{result.message}</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {result.duration && `${result.duration}ms`}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    {new Date(result.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Usage Instructions */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-blue-800 mb-2">💡 Configuration Instructions</h3>
            <div className="text-sm text-blue-700 space-y-2">
              <div>
                <strong>Environment Variables (recommended):</strong>
                <pre className="bg-blue-100 p-2 rounded mt-1 text-xs overflow-x-auto">
{`# .env.local
REACT_APP_FIREBASE_AUTH_EMULATOR_HOST=localhost
REACT_APP_FIREBASE_AUTH_EMULATOR_PORT=9099

# Or use full URL
REACT_APP_FIREBASE_AUTH_EMULATOR_URL=http://localhost:9099

# Force enable/disable emulator
REACT_APP_USE_FIREBASE_EMULATOR=true`}
                </pre>
              </div>
              <div>
                <strong>Common Emulator Commands:</strong>
                <pre className="bg-blue-100 p-2 rounded mt-1 text-xs overflow-x-auto">
{`# Standard setup
firebase emulators:start --only auth

# Custom port
firebase emulators:start --only auth --port 4000

# Specific host binding
firebase emulators:start --host 0.0.0.0 --only auth`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
